'use client'

import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import { Fragment, useState, useEffect, useRef } from 'react'

export function Listbox({
  className,
  placeholder = "-- Select --",
  autoFocus,
  'aria-label': ariaLabel,
  children: options,
  value,
  onChange, 
  ...props
}) {
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [position, setPosition] = useState('bottom'); // Position of dropdown (either 'top' or 'bottom')
  const buttonRef = useRef(null); // Reference to the Listbox button
  const [isOpen, setIsOpen] = useState(false);

  // Update window height on resize
  useEffect(() => {
    const handleResize = () => setWindowHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);  

  // Calculate position based on available space
  const calculatePosition = () => {
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = windowHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;

      if (spaceBelow < 240 && spaceAbove > 240) {
        setPosition('top'); // Position dropdown at the top if there is enough space above
      } else {
        setPosition('bottom'); // Otherwise, position it at the bottom
      }
    }
  };

  // Recalculate position when window height changes or when dropdown opens
  useEffect(() => { 
    calculatePosition();
  }, [windowHeight, isOpen]); // Recalculate when window height changes or dropdown opens

  // Add scroll listener to recalculate position when scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) {
        calculatePosition();
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isOpen]);

  const listboxMaxHeight = Math.min(windowHeight * 0.4, 400); // Max height set to 40% of window or 400px, whichever is smaller

  return (
    <Headless.Listbox value={value} onChange={onChange} {...props}>
      {({ open }) => {
        // Update isOpen state when dropdown opens/closes
        if (open !== isOpen) {
          setIsOpen(open);
        }
        
        return (
          <div className="relative">
            <Headless.Listbox.Button
              ref={buttonRef} // Reference the button for position calculation
              autoFocus={autoFocus}
              data-slot="control"
              aria-label={ariaLabel}
              className={clsx(
                className,
                'group relative block w-full cursor-pointer', 
                'focus:outline-hidden',
                'after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-transparent after:ring-inset data-focus:after:ring-2 data-focus:after:ring-blue-500',
                'data-disabled:opacity-50 data-disabled:before:bg-zinc-950/5 data-disabled:before:shadow-none data-disabled:cursor-not-allowed',
                'text-left min-h-11 sm:min-h-9 pr-[calc(--spacing(7)-1px)] pl-[calc(--spacing(3.5)-1px)] sm:pl-[calc(--spacing(3)-1px)]',
                'py-[calc(--spacing(2.5)-1px)] sm:py-[calc(--spacing(1.5)-1px)]',
                'text-base/6 text-zinc-950 placeholder:text-zinc-500 sm:text-sm/6 dark:text-white',
                'border border-zinc-950/10 group-data-active:border-zinc-950/20 group-data-hover:border-zinc-950/20 dark:border-white/10 dark:group-data-active:border-white/20 dark:group-data-hover:border-white/20',
                'bg-transparent dark:bg-white/5',
                'group-data-invalid:border-red-500 dark:group-data-invalid:border-red-600'
              )}
            >
              <span className="block truncate">
                {value
                  ? options.find((opt) => opt.props?.value === value)?.props?.children
                  : <span className="text-zinc-500">{placeholder}</span>
                }
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                {/* Chevron icon */}
                <svg
                  className="size-5 stroke-zinc-500 group-data-disabled:stroke-zinc-600 sm:size-4 dark:stroke-zinc-400"
                  viewBox="0 0 16 16"
                  aria-hidden="true"
                  fill="none"
                >
                  <path
                    d="M5.75 10.75L8 13L10.25 10.75"
                    strokeWidth={1.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10.25 5.25L8 3L5.75 5.25"
                    strokeWidth={1.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </Headless.Listbox.Button>

            <Headless.Listbox.Options
              className={clsx(
                'absolute z-50 w-full overflow-y-auto rounded-xl p-1',
                'bg-white/75 backdrop-blur-xl dark:bg-zinc-800/75 shadow-lg ring-1 ring-zinc-950/10 dark:ring-white/10 dark:ring-inset',
                'transition-opacity duration-100 ease-in data-closed:data-leave:opacity-0 data-transition:pointer-events-none'
              )}
              style={{
                maxHeight: listboxMaxHeight,  // Dynamically set maxHeight
                top: position === 'bottom' ? '100%' : 'auto', // Set dropdown position based on available space
                bottom: position === 'top' ? '100%' : 'auto', // Position at the top if necessary
                overflowY: 'auto',  // Enable scrolling if content exceeds max height
              }}
            >
              {options}
            </Headless.Listbox.Options>
          </div>
        );
      }}
    </Headless.Listbox>
  )
}

export function ListboxOption({ children, className, ...props }) {
  let sharedClasses = clsx(
    'flex min-w-0 items-center',
    '*:data-[slot=icon]:size-5 *:data-[slot=icon]:shrink-0 sm:*:data-[slot=icon]:size-4',
    '*:data-[slot=icon]:text-zinc-500 group-data-focus/option:*:data-[slot=icon]:text-white dark:*:data-[slot=icon]:text-zinc-400',
    'forced-colors:*:data-[slot=icon]:text-[CanvasText] forced-colors:group-data-focus/option:*:data-[slot=icon]:text-[Canvas]'
  )

  return (
    <Headless.Listbox.Option as={Fragment} {...props}>
      {({ selected, active }) => (
        <div
          className={clsx( 
            'group/option grid cursor-default grid-cols-[--spacing(5)_1fr] items-baseline gap-x-2 rounded-lg py-2.5 pr-3.5 pl-2 sm:grid-cols-[--spacing(4)_1fr] sm:py-1.5 sm:pr-3 sm:pl-1.5',
            'text-base/6 text-zinc-950 sm:text-sm/6 dark:text-white',
            active && 'bg-blue-500',
            'data-disabled:opacity-50'
          )} style={{
            color: active ? 'white' : 'inherit',  // Directly control text color on active state
          }}
        >
          <svg
            className={clsx('relative hidden size-5 self-center stroke-current sm:size-4', selected && 'inline')}
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
          >
            <path d="M4 8.5l3 3L12 4" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className={clsx(className, sharedClasses, 'col-start-2')}>{children}</span>
        </div>
      )}
    </Headless.Listbox.Option>
  )
}

export function ListboxLabel({ className, ...props }) {
  return <span {...props} className={clsx(className, 'ml-2.5 truncate first:ml-0 sm:ml-2 sm:first:ml-0')} />
}

export function ListboxDescription({ className, children, ...props }) {
  return (
    <span
      {...props}
      className={clsx(
        className,
        'flex flex-1 overflow-hidden text-zinc-500 group-data-focus/option:text-white before:w-2 before:min-w-0 before:shrink dark:text-zinc-400'
      )}
    >
      <span className="flex-1 truncate">{children}</span>
    </span>
  )
}